import React, { useMemo, useState } from 'react';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { OwnerButton, OwnerShell } from '../OwnerShell';
import { SelectField, TextAreaField, TextField } from '../OwnerFields';
import { UploadTile } from '../UploadTile';
import { useMyVehicle } from '../../../hooks/useMyVehicles';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { ownerProfile } from '../../../data/ownerProfile';
import type { OwnerNav } from '../../../types/owner';
export function OwnerCorrection({
  nav
}: {
  nav: OwnerNav;
}) {
  const mv = useMyVehicle(nav.vehicleId);
  const {
    submitRequest
  } = useOwnerService();
  const prefillDoc = nav.correctionPrefill.startsWith('doc:') ? nav.correctionPrefill.split(':') : null;
  const v = mv?.vehicle;
  const subjects = useMemo(() => {
    const base: Record<string, string> = {
      'গাড়ির রং': v?.color ?? '',
      'রেজিস্ট্রেশনের তারিখ': v?.regDate ?? '',
      'ইঞ্জিন নম্বর': v?.engine ?? '',
      'চেসিস নম্বর': v?.chassis ?? '',
      'মডেল': `${v?.brand ?? ''} ${v?.model ?? ''}`,
      'মালিকের ঠিকানা': ownerProfile.present
    };
    if (prefillDoc) base[`${prefillDoc[1]} নবায়ন`] = `মেয়াদ: ${prefillDoc[2]}`;
    return base;
  }, [v, prefillDoc]);
  const [subject, setSubject] = useState(prefillDoc ? `${prefillDoc[1]} নবায়ন` : 'গাড়ির রং');
  const [proposed, setProposed] = useState(prefillDoc ? 'মেয়াদ: ১৪/১১/২০২৭ (নবায়নকৃত)' : 'সবুজ-কালো');
  const [reason, setReason] = useState(prefillDoc ? 'নবায়ন সম্পন্ন হয়েছে, নতুন কপি সংযুক্ত করা হলো' : 'রং পরিবর্তন করা হয়েছে; BRTA রেকর্ডে হালনাগাদ সম্পন্ন');
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState('');
  const [rid, setRid] = useState('');
  if (!v) return null;
  const isDoc = subject.endsWith('নবায়ন');
  const submit = () => {
    if (!proposed.trim() || !reason.trim()) return setError('প্রস্তাবিত তথ্য ও কারণ লিখুন');
    if (!uploaded) return setError('সহায়ক ডকুমেন্ট আপলোড করুন');
    const id = submitRequest({
      kind: isDoc ? 'ডকুমেন্ট হালনাগাদ' : 'গাড়ির তথ্য সংশোধন',
      ownerName: ownerProfile.name,
      vehicleReg: v.reg,
      subject,
      current: subjects[subject] ?? '',
      proposed,
      reason,
      vehicleId: v.id,
      attachments: 1
    });
    setRid(id);
  };
  if (rid) {
    return <OwnerShell nav={nav} title="তথ্য সংশোধনের আবেদন" footer={<div className="space-y-2" data-id="element-1432" data-mp="17qa79kv1ww5f"><OwnerButton onClick={() => nav.reset('tracking')} data-id="element-1433" data-mp="17txyuegxr8hi">আবেদনের অবস্থা দেখুন</OwnerButton><OwnerButton variant="outline" onClick={nav.back} data-id="element-1434" data-mp="tylvjiops9oz">ফিরে যান</OwnerButton></div>} data-id="element-1431" data-mp="17rtnjbgpj777">
        <div className="pt-8 text-center" data-id="element-1435" data-mp="ysq3a45t0lr4">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-bdgreen-600 text-white" data-id="element-1436" data-mp="k2snabi132d0"><CheckCircle2 className="h-11 w-11" data-id="element-1437" data-mp="p12k0qjeib0q" /></span>
          <h2 className="mt-4 font-heading text-xl font-bold text-navy-900" data-id="element-1438" data-mp="ozj1eta9vnvs">সংশোধনের আবেদন জমা হয়েছে</h2>
          <p className="mx-auto mt-3 w-fit rounded-lg bg-slate-100 px-4 py-2 font-mono text-base font-bold text-navy-900" data-id="element-1439" data-mp="b3xdkpfrza4y">{rid}</p>
          <p className="mt-1 text-[12px] text-slate-500" data-id="element-1440" data-mp="3sk00x7a9fzzt">ট্র্যাকিং আইডি</p>
          <p className="mt-5 rounded-lg bg-blue-50 p-3 text-[12px] leading-relaxed text-blue-800" data-id="element-1441" data-mp="3smcqa5iclcmw">ডিএমপি পর্যালোচনা ও অনুমোদন না হওয়া পর্যন্ত অনুমোদিত রেকর্ডে কোনো পরিবর্তন হবে না।</p>
        </div>
      </OwnerShell>;
  }
  return <OwnerShell nav={nav} title="তথ্য সংশোধনের আবেদন" footer={<OwnerButton onClick={submit} data-id="element-1443" data-mp="1gl5llncas7h">সংশোধনের আবেদন জমা দিন</OwnerButton>} data-id="element-1442" data-mp="1dywhhksw4n5">
      <div className="space-y-3.5" data-id="element-1444" data-mp="fcf3zaqhlz6c">
        <TextField id="c-reg" label="গাড়ির নম্বর" value={v.reg} readOnly data-id="element-1445" data-mp="aewqr85fsxsj" />
        <SelectField id="c-sub" label="সংশোধনের বিষয়" required value={subject} options={Object.keys(subjects)} onChange={s => {
        setSubject(s);
        setError('');
      }} data-id="element-1446" data-mp="acsr52ekmky2" />
        <TextField id="c-cur" label="বর্তমান তথ্য" value={subjects[subject] ?? ''} readOnly data-id="element-1447" data-mp="k5ri2qxjinku" />
        <TextField id="c-new" label="প্রস্তাবিত নতুন তথ্য" required value={proposed} onChange={setProposed} data-id="element-1448" data-mp="y5j0r4z04s56" />
        <TextAreaField id="c-why" label="সংশোধনের কারণ" required value={reason} onChange={setReason} data-id="element-1449" data-mp="t6pjr2kiaj09" />
        <UploadTile label="সহায়ক ডকুমেন্ট আপলোড" required state={uploaded ? 'uploaded' : 'missing'} fileName="supporting_doc.pdf" onUpload={() => {
        setUploaded(true);
        setError('');
      }} data-id="element-1450" data-mp="2nktm2xe870sp" />
        {error && <p className="text-[12px] font-medium text-red-600" data-id="element-1451" data-mp="2xcpnahblz715">{error}</p>}
        <p className="flex gap-2 rounded-lg bg-amber-50 p-2.5 text-[11.5px] leading-relaxed text-amber-800" data-id="element-1452" data-mp="31djepko9f3ys"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1453" data-mp="24nagk3qz80b1" />অনুমোদিত রেকর্ড স্বয়ংক্রিয়ভাবে পরিবর্তন হবে না — ডিএমপি পর্যালোচনার পর কার্যকর হবে।</p>
      </div>
    </OwnerShell>;
}